import React, { useEffect, useState } from 'react';
import { Badge, Box, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import AiGuideRobot from '../../../components/Reusables/GuidedWalkthrough/AiGuideRobot';
import { BLOGSMITH_DEMO_SCENES } from './homePageContent';

const blink = keyframes`
  0%, 45% { opacity: 1; }
  46%, 100% { opacity: 0; }
`;

const Cursor = () => (
	<Box
		as='span'
		display='inline-block'
		w='2px'
		h='0.9em'
		bg='cyan.300'
		ml='1px'
		verticalAlign='text-bottom'
		animation={`${blink} 0.9s step-end infinite`}
		aria-hidden
	/>
);

const useTypewriter = (text, speed = 22, active = true) => {
	const [index, setIndex] = useState(0);

	useEffect(() => {
		setIndex(0);
	}, [text]);

	useEffect(() => {
		if (!active || index >= text.length) return undefined;
		const timer = window.setTimeout(() => setIndex((i) => i + 1), speed);
		return () => window.clearTimeout(timer);
	}, [active, index, text, speed]);

	return { displayed: text.slice(0, index), isComplete: index >= text.length };
};

export default function BlogsmithTypewriterDemo({ compact = false }) {
	const [sceneIdx, setSceneIdx] = useState(0);
	const scene = BLOGSMITH_DEMO_SCENES[sceneIdx];

	const prompt = useTypewriter(scene.prompt, 18, true);
	const title = useTypewriter(scene.title, 24, prompt.isComplete);
	const outline = useTypewriter(scene.outline, 14, title.isComplete);

	useEffect(() => {
		if (!outline.isComplete) return undefined;
		const timer = window.setTimeout(() => {
			setSceneIdx((i) => (i + 1) % BLOGSMITH_DEMO_SCENES.length);
		}, 3200);
		return () => window.clearTimeout(timer);
	}, [outline.isComplete, sceneIdx]);

	return (
		<Box
			bg='whiteAlpha.100'
			border='1px solid'
			borderColor='whiteAlpha.200'
			rounded='2xl'
			h={{base: 450, xl: 420}}
			p={compact ? 4 : 6}
			backdropFilter='blur(12px)'>
			<HStack spacing={3} mb={4}>
				<Flex
					w={compact ? 10 : 12}
					h={compact ? 10 : 12}
					rounded='xl'
					bg='cyan.400'
					align='center'
					justify='center'
					overflow='hidden'
					flexShrink={0}>
					<AiGuideRobot size={compact ? 48 : 60} />
				</Flex>
				<Box flex={1}>
					<HStack spacing={2} mb={0.5}>
						<Text fontWeight='bold' color='white' fontSize={compact ? 'sm' : 'md'} mb={0}>
							Blogsmith
						</Text>
						<Badge
							colorScheme='green'
							variant='subtle'
							fontSize='10px'
							textTransform='uppercase'
							rounded='full'
							px={2}>
							● Live
						</Badge>
					</HStack>
					<Text fontSize='xs' color='whiteAlpha.600' mb={0} fontFamily='mono'>
						AI · SEO · WordPress autopilot
					</Text>
				</Box>
			</HStack>

			<VStack align='stretch' spacing={3}>
				<Box
					p={3}
					rounded='xl'
					bg='blackAlpha.300'
					border='1px solid'
					borderColor='whiteAlpha.100'>
					<Text fontSize='10px' color='cyan.300' fontWeight='bold' textTransform='uppercase' mb={1}>
						Prompt
					</Text>
					<Text fontSize='sm' color='whiteAlpha.900' mb={0} lineHeight='tall'>
						{prompt.displayed}
						{!prompt.isComplete && <Cursor />}
					</Text>
				</Box>

				{(prompt.isComplete || title.displayed) && (
					<Box
						p={3}
						rounded='xl'
						bg='whiteAlpha.150'
						border='1px solid'
						borderColor={title.isComplete ? 'cyan.400' : 'whiteAlpha.100'}
						transition='border-color 0.3s'>
						<Text fontSize='10px' color='cyan.300' fontWeight='bold' textTransform='uppercase' mb={1}>
							Generated title
						</Text>
						<Text fontSize='sm' fontWeight='bold' color='white' mb={0} lineHeight='tall'>
							{title.displayed}
							{prompt.isComplete && !title.isComplete && <Cursor />}
						</Text>
					</Box>
				)}

				{(title.isComplete || outline.displayed) && (
					<Box p={3} rounded='xl' bg='whiteAlpha.50' border='1px solid' borderColor='whiteAlpha.100'>
						<Text fontSize='10px' color='whiteAlpha.500' fontWeight='bold' textTransform='uppercase' mb={1}>
							SEO outline
						</Text>
						<Text fontSize='xs' color='whiteAlpha.800' mb={0}>
							{outline.displayed}
							{title.isComplete && !outline.isComplete && <Cursor />}
						</Text>
					</Box>
				)}

				{outline.isComplete && (
					<HStack
						spacing={2}
						mt={{base: 2, md: 4}}
						px={3}
						py={2}
						rounded='full'
						bg='green.500'
						alignSelf='flex-start'
						boxShadow='0 0 20px rgba(72, 187, 120, 0.35)'>
						<Text fontSize='xs' fontWeight='bold' color='white' mb={0}>
							✓ Ready to publish to WordPress
						</Text>
					</HStack>
				)}
			</VStack>
		</Box>
	);
}
